import {
  InputOtp,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  useDisclosure,
} from '@heroui/react';
import { FaInfoCircle } from 'react-icons/fa';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { useMemo, useState } from 'react';
import { validateVerificationCode } from '../../../../service/twoFactorAuthService';
import { useCountdown } from '../../../../hook/commonHook';
import { RiIndeterminateCircleFill } from 'react-icons/ri';
import { useAuth } from '../../../../context/AuthContext';
import {
  useRequestAccountDeactivationOtp,
  useResendAccountDeactivationAndDeleteOtp,
  useVerifyAccountDeactivationOtp,
} from '../../../../hook/useMutationData';
import {
  formatCurrency,
  formatDisplayDate,
  isMatch,
} from '../../../../utils/helper';
import { IBookingQuery } from '../../../../utils/type';
import { useFetchAllBookingsByQuery } from '../../../../hook/useQueryData';
import CommonTableModal, {
  TableColumn,
} from '../../../components/CommonTableModal';
import CustomChip from '../../../components/CustomChip';
import { BOOKING_STATUS_COLOR_MAP } from '../../../../utils/enum';
import CustomAlert from '../../../components/CustomAlert';
import CustomTextArea from '../../../components/common/ui/CustomTextArea';
import { handleLogin } from '../../../components/auth/apiAuth';

const ACCOUNT_STATE_REASON = {
  PRIVACY_CONCERNS: 'privacy_concerns',
  NOT_USEFUL: 'no_longer_needed',
  UI_UX_ISSUES: 'difficult_to_use',
  COST_EXPENSIVE: 'too_expensive',
  SWITCHING_COMPETITOR: 'switching_to_competitor',
  OTHER: 'other',
} as const;

type AccountStateReason =
  (typeof ACCOUNT_STATE_REASON)[keyof typeof ACCOUNT_STATE_REASON];

const RESEND_DELAY = 60;
const OTHER_FEEDBACK_MAX_LENGTH = 500;

interface UserRow {
  id: string | number;
  referenceCode: string;
  customerId: string;
  firstName: string;
  providerId: string;
  bookingStatus: string;
  serviceTitle: string;
  email?: string;
}

const columns: TableColumn<UserRow>[] = [
  {
    key: 'rowNumber',
    label: '#',
    width: '2%',
    align: 'center',
    render: (_, __, index?: number) => (
      <span className="text-default-400 font-medium">
        {index !== undefined ? index + 1 : '—'}
      </span>
    ),
  },
  {
    key: 'referenceCode',
    label: 'Booking Id',
    width: '10%',
    align: 'left',
  },
  {
    key: 'bookingStatus',
    label: 'Booking Status',
    width: '10%',
    align: 'left',
    render: (value: any) => (
      <CustomChip
        label={value}
        size="sm"
        variant="flat"
        color={BOOKING_STATUS_COLOR_MAP[value]}
        className="capitalize"
      />
    ),
  },
  {
    key: 'serviceTitle',
    label: 'Service Title',
    width: '15%',
    align: 'left',
  },
  { key: 'customerId', label: 'Customer ID', width: '10%', align: 'left' },
  { key: 'firstName', label: 'Customer Name', width: '12%', align: 'left' },

  { key: 'appointmentDate', label: 'Date', width: '10%', align: 'left' },
  { key: 'appointmentTime', label: 'Time', width: '12%', align: 'left' },
  { key: 'total', label: 'Total', width: '10%', align: 'left' },
];

const AccountDeactivate = () => {
  const { user, userId } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: bookingIsOpen,
    onOpen: bookingOnOpen,
    onOpenChange: bookingOnOpenChange,
    onClose: bookingOnClose,
  } = useDisclosure();

  const [page, setPage] = useState(1);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [deactivateStep, setDeactivateStep] = useState<
    'reason' | 'request' | 'verify'
  >('reason');
  const [reason, setReason] = useState<AccountStateReason | undefined>();
  const [otherFeedback, setOtherFeedback] = useState('');
  const [securityToken, setSecurityToken] = useState<string | undefined>();

  const [alertMessage, setAlertMessage] = useState<{
    msg: string;
    type: 'error' | 'success';
  } | null>(null);

  const query: IBookingQuery = {
    fullProfile: false,
    filters: {
      customerId: userId,
      bookingStatus: [
        'Confirmed',
        'Pending',
        'InProgress',
        'Waiting',
        'Rescheduled',
      ],
    },
    isDeleted: false,
    isVisible: true,
    ownerId: userId ?? '',
    bookingProjection:
      'referenceCode,providerId,customerId,serviceId,staffId,personalInfo.firstName,personalInfo.lastName,bookingStatus,appointmentDate,appointmentTimeFrom,appointmentTimeTo,total',
    serviceProjection: 'serviceTitle,serviceId,categoryId,category.categoryId',
    staffProjection: 'staffId,firstName,lastName',
    withMongoId: false,
    page: page,
    limit: 15,
  };

  const { countdown, startCountdown, resetCountdown, isActive } =
    useCountdown();

  const { mutateAsync: requestOtp, isPending: isOtpRequestLoading } =
    useRequestAccountDeactivationOtp();
  const { mutateAsync: verifyOtp, isPending: isOtpVerifyLoading } =
    useVerifyAccountDeactivationOtp();
  const { mutateAsync: resendOtp, isPending: isResendOtpLoading } =
    useResendAccountDeactivationAndDeleteOtp();

  const { data, isPending: isBookingLoading } = useFetchAllBookingsByQuery({
    data: query,
    enable: !!userId,
  });

  console.log('AccountDeactivate Component Rendered', data);

  const normalizedBookings = useMemo(() => {
    const bookingsArray =
      data?.bookings || (Array.isArray(data?.bookings) ? data?.bookings : null);

    if (!bookingsArray) return [];

    return bookingsArray.map((booking: any) => ({
      id: booking.referenceCode,
      referenceCode: booking.referenceCode || '—',
      customerId: booking.customerId || '—',
      firstName: booking.personalInfo?.firstName || '—',
      bookingStatus: booking.bookingStatus || '—',
      serviceTitle: booking.service?.serviceTitle || '—',
      appointmentDate: formatDisplayDate(booking.appointmentDate),
      appointmentTime:
        ` ${booking.appointmentTimeFrom} - ${booking.appointmentTimeTo}` || '—',
      total: formatCurrency(booking.total) || '—',
    }));
  }, [data]);

  const handleOpenModal = () => {
    if (isBookingLoading) return;

    console.log('Normalized Booking Data ready for Modal:', normalizedBookings);

    if (normalizedBookings.length > 0) {
      console.log(
        'Active bookings found for user, opening booking modal instead.'
      );
      bookingOnOpen();
      return;
    }

    setAlertMessage(null);
    setSecurityToken(undefined);
    setConfirmEmail('');
    setVerificationCode('');
    setOtherFeedback('');
    resetCountdown();
    onOpen();
  };

  const currentUserId = user?.userId?.trim();
  const currentEmail = user?.email?.trim();

  //STEP -1 - REASON
  const handleDeactivateReason = async () => {
    if (!reason) {
      setAlertMessage({
        msg: 'Please select a reason for deactivating your account.',
        type: 'error',
      });
      return;
    }
    setDeactivateStep('request');
  };

  //STEP - 2 -REQUEST OTP
  const handleRequestVerificationCode = async () => {
    setAlertMessage(null);
    resetCountdown();

    try {
      if (!isMatch(user?.email, confirmEmail)) {
        setAlertMessage({
          msg: 'The email address does not match.',
          type: 'error',
        });
        return;
      }

      if (!currentUserId || !currentEmail) {
        setAlertMessage({
          msg: 'Unable to identify user details. Please re-login.',
          type: 'error',
        });
        return;
      }

      const result = await requestOtp({
        accountType: 'customer',
        reason: reason!,
        ...(reason === ACCOUNT_STATE_REASON.OTHER
          ? { feedback: otherFeedback.trim() }
          : {}),
      });

      console.log('OTP Request Result:', result);

      setSecurityToken(result.securityToken || undefined);
      setAlertMessage({ msg: result.message, type: 'success' });
      setDeactivateStep('verify');
      startCountdown(RESEND_DELAY);
    } catch (error: any) {
      console.error('Error requesting account deactivation:', error);

      let errorMessage =
        error.message ||
        'Failed to request account deactivation. Please try again.';

      setAlertMessage({ msg: errorMessage, type: 'error' });
      setSecurityToken(undefined);
      setDeactivateStep('request');
      resetCountdown();
    }
  };

  //  STEP - 3 - VERIFY OTP AND CONFIRM DEACTIVATION
  const handleConfirmDelete = async () => {
    setAlertMessage(null);

    if (!validateVerificationCode(verificationCode)) {
      setAlertMessage({
        msg: 'Please enter a valid 6-digit verification code.',
        type: 'error',
      });
      return;
    }

    if (!currentUserId || !currentEmail || !securityToken) {
      setAlertMessage({
        msg: 'Unable to identify user details. Please re-login and try again.',
        type: 'error',
      });
      return;
    }

    try {
      const payload = {
        accountType: 'customer',
        otp: verificationCode,
        securityToken,
      };
      const result = await verifyOtp(payload);

      setAlertMessage({
        msg: result?.message || 'Account deactivation successful.',
        type: 'success',
      });

      onClose();
      handleLogin();
    } catch (error: any) {
      console.error('Error requesting account deactivation:', error);

      let errorMessage =
        error.message ||
        'Failed to request account deactivation. Please try again.';

      setAlertMessage({ msg: errorMessage, type: 'error' });
    }
  };

  //RESEND CODE
  const handleResendVerificationCode = async () => {
    try {
      resetCountdown();

      const result = await resendOtp({
        accountType: 'customer',
        action: 'deactivate',
      });

      setSecurityToken(result.securityToken || undefined);
      startCountdown(RESEND_DELAY);
    } catch (error: any) {
      console.error('Error resending verification code:', error);

      let errorMessage =
        error.message || 'Something went wrong. Please try again later';

      setAlertMessage({ msg: errorMessage, type: 'error' });
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between items-start gap-3 sm:mt-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
            <RiIndeterminateCircleFill className="text-yellow-600" size={18} />
          </div>
          <div className="flex flex-col items-start justify-start">
            <h3 className="text-sm font-semibold text-gray-800">
              Deactivate Account
            </h3>
            <p className="text-caption">
              Temporarily deactivate your account. You can reactivate it at any
              time by logging back in.
            </p>
          </div>
        </div>

        <div className="flex justify-end w-full sm:w-auto">
          <CustomButton
            color="danger"
            label="Deactivate"
            variant="flat"
            className="mt-1"
            onPress={() => {
              handleOpenModal();
            }}
          />
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setAlertMessage(null);
          setSecurityToken(undefined);
          setConfirmEmail('');
          setVerificationCode('');
          setReason(undefined);
          setOtherFeedback('');
          setDeactivateStep('reason');
          resetCountdown();
          onClose();
        }}
        placement="center"
        backdrop="blur"
        size="lg"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Account Deactivate
          </ModalHeader>
          <ModalBody>
            {alertMessage != null && (
              <CustomAlert
                description={alertMessage.msg}
                color={alertMessage.type === 'success' ? 'success' : 'danger'}
              />
            )}

            {deactivateStep === 'reason' ? (
              <div className="mt-3">
                <p className="text-sm font-medium mb-3">
                  What's the reason for deactivating your account?
                </p>
                <div className="flex justify-start items-center">
                  <RadioGroup
                    size="sm"
                    onValueChange={(value) => {
                      const selectedReason = value as AccountStateReason;
                      setReason(selectedReason);
                      if (selectedReason !== ACCOUNT_STATE_REASON.OTHER) {
                        setOtherFeedback('');
                      }
                    }}
                    value={reason}
                  >
                    {Object.entries(ACCOUNT_STATE_REASON).map(
                      ([key, value]) => (
                        <Radio key={key} value={value} className="text-sm">
                          {value.replace(/_/g, ' ').charAt(0).toUpperCase() +
                            value.replace(/_/g, ' ').slice(1)}
                        </Radio>
                      )
                    )}
                  </RadioGroup>
                </div>
                {reason === ACCOUNT_STATE_REASON.OTHER && (
                  <div className="mt-4">
                    <CustomTextArea
                      label="Tell us more (optional)"
                      placeholder="Share your reason..."
                      value={otherFeedback}
                      onValueChange={(value) =>
                        setOtherFeedback(
                          value.slice(0, OTHER_FEEDBACK_MAX_LENGTH)
                        )
                      }
                      maxLength={OTHER_FEEDBACK_MAX_LENGTH}
                      description={`${otherFeedback.length}/${OTHER_FEEDBACK_MAX_LENGTH} characters`}
                      minRows={3}
                      maxRows={5}
                    />
                  </div>
                )}
              </div>
            ) : (
              deactivateStep === 'request' && (
                <div>
                  <div className="bg-orange-50 p-4 rounded border border-orange-200 text-orange-800 flex items-start gap-3 text-sm mb-2">
                    <FaInfoCircle className="mt-0.5 text-orange-600" />
                    <div>
                      <h4 className="font-semibold mb-2">
                        Your account will be temporarily deactivated
                      </h4>
                      <ul className="list-none space-y-3 text-sm text-gray-700">
                        <li className="flex text-xs items-start gap-2.5">
                          <span className="text-red-500 font-bold mt-0.5">
                            ✕
                          </span>
                          <span>
                            You will be <strong>logged out immediately</strong>{' '}
                            and your account will become deactivate until
                            reactivated.
                          </span>
                        </li>

                        <li className="flex text-xs items-start gap-2.5">
                          <span className="text-red-500 font-bold mt-0.5">
                            ✕
                          </span>
                          <span>
                            Your profile, saved settings, and account data will
                            remain securely stored and can be restored after
                            reactivation.
                          </span>
                        </li>

                        <li className="flex text-xs items-start gap-2.5">
                          <span className="text-red-500 font-bold mt-0.5">
                            ✕
                          </span>
                          <span>
                            While your account is deactivated, certain features
                            and services within the system may{' '}
                            <strong> not be accessible</strong>.
                          </span>
                        </li>

                        <li className="flex text-xs items-start gap-2.5">
                          <span className="text-red-500 font-bold mt-0.5">
                            ✕
                          </span>
                          <span>
                            You can <strong>reactivate your account</strong> at
                            any time by signing in again or using the account
                            reactivation option.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )
            )}

            {deactivateStep === 'request' && (
              <div className="mt-3">
                <CustomInput
                  isRequired
                  label="Type your account email to confirm"
                  type="email"
                  placeholder={user?.email || 'your@email.com'}
                  value={confirmEmail}
                  onValueChange={setConfirmEmail}
                />
              </div>
            )}

            {deactivateStep === 'verify' && (
              <>
                <div className="mt-1 space-y-3">
                  <div className="flex justify-center items-center">
                    <InputOtp
                      length={6}
                      isRequired
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onValueChange={setVerificationCode}
                      isDisabled={isOtpVerifyLoading}
                      variant="bordered"
                      errorMessage="Invalid OTP code"
                    />
                  </div>

                  <div>
                    <p className="text-body mb-3">
                      Didn't receive a code?{' '}
                      <strong
                        className={`hover:underline cursor-pointer text-primary transition-opacity ${
                          isActive || isResendOtpLoading || isOtpVerifyLoading
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {isResendOtpLoading ? (
                          <span className="inline-flex items-baseline gap-2 ">
                            Sending...
                          </span>
                        ) : (
                          <p
                            onClick={
                              !isActive
                                ? handleResendVerificationCode
                                : undefined
                            }
                            className={`inline-flex items-baseline gap-2  transition-opacity ${
                              !isActive
                                ? 'hover:underline cursor-pointer text-primary'
                                : 'cursor-not-allowed text-gray-500'
                            }`}
                          >
                            {isActive
                              ? `Resend in ${countdown}s`
                              : 'Resend Code'}
                          </p>
                        )}
                      </strong>
                    </p>
                  </div>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <CustomButton
              color="default"
              variant="flat"
              label="Cancel"
              onPress={() => {
                setSecurityToken(undefined);
                setConfirmEmail('');
                setReason(undefined);
                setOtherFeedback('');
                setVerificationCode('');
                setDeactivateStep('reason');
                resetCountdown();
                onClose();
              }}
              isDisabled={isOtpRequestLoading || isOtpVerifyLoading}
            />

            {deactivateStep === 'reason' && (
              <CustomButton
                color="danger"
                variant="flat"
                label="Next"
                onPress={handleDeactivateReason}
              />
            )}

            {deactivateStep === 'request' && (
              <CustomButton
                color="primary"
                variant="flat"
                label="Back"
                onPress={() => {
                  setAlertMessage(null);
                  setDeactivateStep('reason');
                }}
              />
            )}

            {deactivateStep === 'request' && (
              <CustomButton
                color="danger"
                variant="solid"
                label="Request code"
                onPress={handleRequestVerificationCode}
                isLoading={isOtpRequestLoading}
              />
            )}

            {deactivateStep === 'verify' && (
              <>
                <CustomButton
                  color="danger"
                  variant="solid"
                  label="Confirm Deactivation"
                  onPress={() => {
                    handleConfirmDelete();
                    resetCountdown();
                  }}
                  isDisabled={isOtpVerifyLoading}
                  isLoading={isOtpVerifyLoading}
                />
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CommonTableModal<UserRow>
        isOpen={bookingIsOpen}
        onClose={bookingOnClose}
        onOpenChange={bookingOnOpenChange}
        title="Active Booking Report"
        columns={columns}
        data={normalizedBookings}
        isLoading={isBookingLoading}
        caption="Organizational users log summary matrix"
        currentPage={page}
        totalPages={data?.totalPages || 1}
        totalData={data?.total || 0}
        onPageChange={(targetPage) => setPage(targetPage)}
      />
    </>
  );
};

export default AccountDeactivate;
